import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import type { IconDefinition } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type React from 'react';
import { useIsDarkMode } from 'src/common/hooks/use-is-dark-mode';
import { StyledAccordionBody, StyledAccordionChevron, StyledAccordionContent, StyledAccordionHeader, StyledAccordionHeaderContent, StyledAccordionItem, StyledAccordionTitle, StyledCommandLabel, StyledCommandList, StyledCommandWrapper, StyledFlexibleCommandWrapper, StyledInlineCommand, StyledReason, StyledRecommendation, StyledSectionIcon } from '../post-room-completion-feedback-step.styles';
interface SharedAccordionProps {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  variant: 'good' | 'improvement';
  icon: IconDefinition;
  content: {
    commands?: string[];
    command?: string;
    reason?: string;
    recommendation?: string;
    commandLabel?: string;
  };
}

// Helper function to parse text and convert backticks to inline code
const parseTextWithBackticks = (text: string, isDarkMode: boolean): React.ReactNode => {
  if (!text) return null;
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((part, idx) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      // Remove the backticks and render as inline command
      const command = part.slice(1, -1);
      return <StyledInlineCommand key={`command-${command}-${part.length}-${idx}`} $isDarkMode={isDarkMode}>
          {command}
        </StyledInlineCommand>;
    }
    return <span key={`text-${part.slice(0, 10)}-${part.length}-${idx}`}>{part}</span>;
  });
};
export const SharedAccordion = ({
  title,
  isOpen,
  onToggle,
  variant,
  icon,
  content
}: SharedAccordionProps) => {
  const isDarkMode = useIsDarkMode();
  const renderContent = () => {
    if (variant === 'good' && content.command) {
      return <>
          <StyledFlexibleCommandWrapper>
            <StyledSectionIcon $variant="good">
              <FontAwesomeIcon icon={icon} />
            </StyledSectionIcon>
            <StyledCommandLabel>Good Command:</StyledCommandLabel>
            <StyledInlineCommand $isDarkMode={isDarkMode}>{content.command}</StyledInlineCommand>
          </StyledFlexibleCommandWrapper>
          {content.reason && <StyledReason>{parseTextWithBackticks(content.reason, isDarkMode)}</StyledReason>}
        </>;
    }
    if (variant === 'improvement') {
      return <>
          {content.commands && content.commands.length > 0 && <StyledCommandWrapper>
              {content.commands.length === 1 ? <StyledFlexibleCommandWrapper>
                  <StyledSectionIcon $variant="improvement">
                    <FontAwesomeIcon icon={icon} />
                  </StyledSectionIcon>
                  <StyledCommandLabel>{content.commandLabel || 'Missing Command'}:</StyledCommandLabel>
                  <StyledInlineCommand $isDarkMode={isDarkMode}>{content.commands[0]}</StyledInlineCommand>
                </StyledFlexibleCommandWrapper> : <>
                  <StyledFlexibleCommandWrapper>
                    <StyledSectionIcon $variant="improvement">
                      <FontAwesomeIcon icon={icon} />
                    </StyledSectionIcon>
                    <StyledCommandLabel>{content.commandLabel || 'Missing Commands'}:</StyledCommandLabel>
                  </StyledFlexibleCommandWrapper>
                  <StyledCommandList>
                    {content.commands.map(command => <StyledInlineCommand key={`command-${command}`} $isDarkMode={isDarkMode}>
                        {command}
                      </StyledInlineCommand>)}
                  </StyledCommandList>
                </>}
            </StyledCommandWrapper>}
          {content.recommendation && <StyledRecommendation>{parseTextWithBackticks(content.recommendation, isDarkMode)}</StyledRecommendation>}
        </>;
    }
    return null;
  };
  return <StyledAccordionItem $isOpen={isOpen} data-sentry-element="StyledAccordionItem" data-sentry-component="SharedAccordion" data-sentry-source-file="shared-accordion.tsx">
      <StyledAccordionHeader onClick={onToggle} data-expanded={isOpen} $isDarkMode={isDarkMode} data-sentry-element="StyledAccordionHeader" data-sentry-source-file="shared-accordion.tsx">
        <StyledAccordionHeaderContent data-sentry-element="StyledAccordionHeaderContent" data-sentry-source-file="shared-accordion.tsx">
          <StyledAccordionTitle $isDarkMode={isDarkMode} data-sentry-element="StyledAccordionTitle" data-sentry-source-file="shared-accordion.tsx">
            {parseTextWithBackticks(title, isDarkMode)}
          </StyledAccordionTitle>
        </StyledAccordionHeaderContent>
        <StyledAccordionChevron data-sentry-element="StyledAccordionChevron" data-sentry-source-file="shared-accordion.tsx">
          <FontAwesomeIcon icon={faChevronDown} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="shared-accordion.tsx" />
        </StyledAccordionChevron>
      </StyledAccordionHeader>
      <StyledAccordionContent $isOpen={isOpen} data-sentry-element="StyledAccordionContent" data-sentry-source-file="shared-accordion.tsx">
        <StyledAccordionBody data-sentry-element="StyledAccordionBody" data-sentry-source-file="shared-accordion.tsx">{renderContent()}</StyledAccordionBody>
      </StyledAccordionContent>
    </StyledAccordionItem>;
};